import { useState, useEffect, type ReactElement } from 'react'
import * as Sentry from '@sentry/react'
import MuiBox from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Label from '../../../../atoms/display/Label'

/** DocxViewerのプロパティ */
export interface DocxViewerProps {
  /** ドキュメントのURL */
  readonly url: string
  /** ビューアの高さ */
  readonly height: number | string
  /** PC版かどうか */
  readonly isPC: boolean
}

/** PC版のビューア幅 */
const VIEWER_WIDTH_PC = 640 as const

/**
 * DOCXファイルをクライアントサイドでレンダリングするコンポーネント
 * mammoth.jsを使用してDOCXをHTMLに変換して表示する
 *
 * Microsoft Office Onlineビューアの代わりにクライアントサイドで
 * レンダリングすることで、S3署名付きURLでも表示可能にする
 */
export default function DocxViewer({ url, height, isPC }: DocxViewerProps): ReactElement {
  const [html, setHtml] = useState<string>('')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let isMounted = true

    async function loadDocument(): Promise<void> {
      try {
        setIsLoading(true)
        setError(null)

        // URLからファイルをfetch
        const response = await fetch(url)
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const arrayBuffer = await response.arrayBuffer()

        // mammothを遅延読み込みしてHTMLに変換
        // バンドルサイズ最適化のため、DOCXプレビュー時のみ読み込む
        const mammoth = await import('mammoth')
        const result = await mammoth.convertToHtml({ arrayBuffer })

        if (isMounted) {
          setHtml(result.value)
        }
      } catch (err) {
        // Sentryにエラーを通知（調査用のコンテキスト情報を含む）
        Sentry.captureException(err, {
          tags: {
            component: 'DocxViewer',
            action: 'loadDocument',
          },
          extra: {
            url,
          },
        })

        if (isMounted) {
          setError(err instanceof Error ? err.message : 'unknown error')
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    void loadDocument()

    return () => {
      isMounted = false
    }
  }, [url])

  // ローディング中
  if (isLoading) {
    return (
      <MuiBox
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: isPC ? VIEWER_WIDTH_PC : '100%',
          height,
        }}
      >
        <CircularProgress />
      </MuiBox>
    )
  }

  // エラー時
  if (error !== null) {
    return (
      <Label text="エラーが発生しました。ページをリロードしてください。繰り返し発生する場合はサポートにお問い合わせください。" />
    )
  }

  // 正常表示
  return (
    <MuiBox
      sx={{
        width: isPC ? VIEWER_WIDTH_PC : '100%',
        height,
        overflow: 'auto',
        backgroundColor: '#fff',
        padding: 2,
        // mammothが生成するHTMLのスタイル調整
        '& p': {
          margin: '0.5em 0',
        },
        '& table': {
          borderCollapse: 'collapse',
          width: '100%',
        },
        '& td, & th': {
          border: '1px solid #ddd',
          padding: '8px',
        },
        '& img': {
          maxWidth: '100%',
          height: 'auto',
        },
      }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}
